import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  ArrowLeft,
  CalendarDays,
  Building2,
  ClipboardList,
  Pencil,
  RepeatIcon,
  Loader2,
  User,
  Clock,
  WifiOff,
  Camera,
  Trash2,
} from 'lucide-react'
import { useLiveQuery } from 'dexie-react-hooks'
import { usePickup, useUpdatePickup, useDeletePickup } from '@/hooks/queries/usePickups'
import { useWasteTypes } from '@/hooks/queries/useWasteTypes'
import { useOnline } from '@/hooks/useOnline'
import { getCachedPickup, getDraftByPickupId } from '@/lib/db'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getStatusColor, formatKilos, cn } from '@/lib/utils'
import { getSegregationOption } from '@/lib/segregation'
import { useAuth } from '@/context/AuthContext'
import { usePickupOptions } from '@/hooks/queries/usePickupOptions'
import type { Pickup, PickupRecordDraft, PickupStatus } from '@/types'

function formatDateTime(value: string) {
  return format(new Date(value), "d 'de' MMM yyyy, HH:mm", { locale: es })
}

function getErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    return (error as { response?: { status?: number } }).response?.status
  }

  return undefined
}

interface DraftRecordViewProps {
  draft: PickupRecordDraft
  wasteTypeName: (id: number) => string
}

function DraftRecordView({ draft, wasteTypeName }: DraftRecordViewProps) {
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
        <WifiOff className="mt-0.5 h-4 w-4 shrink-0" />
        <p>Registro pendiente de sincronizar</p>
      </div>

      <div className="grid gap-3 text-sm sm:grid-cols-2">
        {draft.started_at && (
          <div className="flex items-start gap-2">
            <Clock className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-muted-foreground">Inicio</p>
              <p className="font-medium">{formatDateTime(draft.started_at)}</p>
            </div>
          </div>
        )}
        {draft.finished_at && (
          <div className="flex items-start gap-2">
            <Clock className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-muted-foreground">Término</p>
              <p className="font-medium">{formatDateTime(draft.finished_at)}</p>
            </div>
          </div>
        )}
      </div>

      {draft.notes && (
        <div>
          <p className="text-sm text-muted-foreground mb-1">Notas del operador</p>
          <p className="text-sm bg-gray-50 rounded-md p-3">{draft.notes}</p>
        </div>
      )}

      {draft.items.length > 0 && (
        <div>
          <p className="text-sm font-medium mb-2">Ítems registrados</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="pb-2 text-left font-medium text-muted-foreground">Residuo</th>
                  <th className="pb-2 text-left font-medium text-muted-foreground">Kilos</th>
                  <th className="pb-2 text-left font-medium text-muted-foreground">Estado</th>
                  <th className="pb-2 text-left font-medium text-muted-foreground">Notas</th>
                  <th className="pb-2 text-left font-medium text-muted-foreground">Fotos</th>
                </tr>
              </thead>
              <tbody>
                {draft.items.map((item, idx) => {
                  const segOption = item.segregation_status
                    ? getSegregationOption(item.segregation_status)
                    : null
                  const SegIcon = segOption?.icon
                  return (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="py-3 pr-3">{wasteTypeName(item.waste_type_id)}</td>
                      <td className="py-3 pr-3 whitespace-nowrap">
                        {formatKilos(Number(item.kilos))}
                      </td>
                      <td className="py-3 pr-3">
                        {segOption ? (
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
                              segOption.selectedClassName,
                            )}
                          >
                            {SegIcon && <SegIcon className="h-3 w-3" />}
                            {segOption.label}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-3 text-muted-foreground">
                        {item.notes || '—'}
                      </td>
                      <td className="py-3">
                        {item.photos.length > 0 ? (
                          <span className="inline-flex items-center gap-1 text-muted-foreground" data-testid="photo-count">
                            <Camera className="h-3.5 w-3.5" />
                            {item.photos.length}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export function PickupDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const pickupId = id ? parseInt(id, 10) : 0
  const { role } = useAuth()
  const isOnline = useOnline()

  const { data: pickup, error, isError, isLoading } = usePickup(pickupId)
  const { data: pickupOptions } = usePickupOptions()
  const updateMutation = useUpdatePickup()
  const deleteMutation = useDeletePickup()
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [cachedPickup, setCachedPickup] = useState<Pickup | null>(null)
  const [loadingCachedPickup, setLoadingCachedPickup] = useState(false)
  const errorStatus = getErrorStatus(error)

  const localDraft = useLiveQuery(
    () => (pickupId ? getDraftByPickupId(pickupId) : undefined),
    [pickupId],
  )
  const { data: wasteTypes } = useWasteTypes()
  const wasteTypeName = (wasteTypeId: number) =>
    wasteTypes?.find((w) => w.id === wasteTypeId)?.name ?? `Tipo #${wasteTypeId}`

  useEffect(() => {
    if (!pickupId) return

    if (pickup) {
      setCachedPickup(null)
      setLoadingCachedPickup(false)
      return
    }

    if (isOnline && !isError) {
      return
    }

    if (errorStatus === 404) {
      setCachedPickup(null)
      setLoadingCachedPickup(false)
      return
    }

    let cancelled = false
    setLoadingCachedPickup(true)

    getCachedPickup(pickupId)
      .then((cached) => {
        if (!cancelled) {
          setCachedPickup(cached ?? null)
        }
      })
      .catch((err) => {
        console.error('Error cargando retiro offline:', err)
        if (!cancelled) {
          setCachedPickup(null)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCachedPickup(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [pickupId, pickup, isOnline, isError, errorStatus])

  const activePickup = pickup ?? cachedPickup
  const isShowingCachedPickup = !pickup && Boolean(cachedPickup)
  const isOfflineMiss = !activePickup && !isLoading && !loadingCachedPickup && (!isOnline || (isError && !errorStatus))

  const handleStatusChange = async (newStatus: PickupStatus) => {
    if (!activePickup || isShowingCachedPickup) return
    setStatusError(null)
    try {
      await updateMutation.mutateAsync({
        id: activePickup.id,
        data: { status: newStatus },
      })
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      setStatusError(
        axiosErr?.response?.data?.message ?? 'No se pudo cambiar el estado del retiro.',
      )
    }
  }

  const handleDelete = async () => {
    if (!activePickup) return
    setDeleteError(null)
    try {
      await deleteMutation.mutateAsync(activePickup.id)
      navigate('/pickups')
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      setDeleteError(
        axiosErr?.response?.data?.message ?? 'Ocurrió un error al eliminar el retiro.',
      )
    }
  }

  if ((isLoading || loadingCachedPickup) && !activePickup) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    )
  }

  if (isOfflineMiss) {
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <WifiOff className="mb-3 h-8 w-8 text-orange-500" />
        <p className="font-medium text-gray-900">Retiro no disponible sin conexión</p>
        <p className="mt-1 max-w-md text-sm text-gray-500">
          Conéctate a internet y vuelve a abrir el calendario para descargar este retiro.
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/pickups')}
        >
          Volver al calendario
        </Button>
      </div>
    )
  }

  if (isError && errorStatus && errorStatus >= 500 && !activePickup) {
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <p className="font-medium text-gray-900">Error al cargar el retiro</p>
        <p className="mt-1 text-sm text-gray-500">
          Ocurrió un error en el servidor. Intenta nuevamente.
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/pickups')}
        >
          Volver al calendario
        </Button>
      </div>
    )
  }

  if (!activePickup) {
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <p className="text-gray-500">Retiro no encontrado</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/pickups')}
        >
          Volver al calendario
        </Button>
      </div>
    )
  }

  const scheduledDate = new Date(activePickup.scheduled_date + 'T00:00:00')
  const record = activePickup.pickup_record ?? null

  return (
    <>
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Encabezado */}
        <div className="flex flex-wrap items-start gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/pickups')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">
                Retiro #{activePickup.id}
              </h1>
              <Badge className={cn(getStatusColor(activePickup.status))}>
                {activePickup.status_label}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {format(scheduledDate, "EEEE d 'de' MMMM yyyy", { locale: es })}
            </p>
          </div>
          {role === 'admin' && !isShowingCachedPickup && (
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => navigate(`/pickups/${activePickup.id}/edit`)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Editar
            </Button>
          )}
        </div>

        {isShowingCachedPickup && (
          <div className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
            <WifiOff className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              Estás viendo información guardada sin conexión. Los cambios más recientes
              estarán disponibles cuando recuperes conexión.
            </p>
          </div>
        )}

        {/* Grid principal: izquierda (cliente + detalles) / derecha (registro) */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Columna izquierda */}
          <div className="space-y-6 lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-green-600" />
                  Cliente
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activePickup.client ? (
                  <div>
                    <p className="font-medium">{activePickup.client.trade_name}</p>
                    {activePickup.client_branch?.name && (
                      <p className="text-sm text-foreground/80">{activePickup.client_branch.name}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {activePickup.client.rut}
                    </p>
                    {(activePickup.client_branch?.address ?? activePickup.client.address) && (
                      <p className="text-sm text-muted-foreground">
                        {activePickup.client_branch?.address ?? activePickup.client.address}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {activePickup.client_branch?.name
                      ? `Sucursal ${activePickup.client_branch.name}`
                      : `Cliente #${activePickup.client_id}`}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-green-600" />
                  Detalles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 text-sm sm:grid-cols-2">
                  <div>
                    <p className="text-muted-foreground">Fecha programada</p>
                    <p className="font-medium">
                      {format(scheduledDate, "d 'de' MMMM yyyy", { locale: es })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Estado</p>
                    {role === 'admin' && !isShowingCachedPickup ? (
                      <>
                        <Select
                          value={activePickup.status}
                          onValueChange={(v) => handleStatusChange(v as PickupStatus)}
                          disabled={updateMutation.isPending}
                        >
                          <SelectTrigger className="h-8 w-auto border-0 p-0 font-medium focus:ring-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(pickupOptions?.statuses ?? [])
                              .filter(
                                (opt) =>
                                  opt.value === activePickup.status ||
                                  activePickup.allowed_status_transitions?.includes(
                                    opt.value,
                                  ),
                              )
                              .map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {statusError && (
                          <p className="mt-1 text-xs text-red-600">{statusError}</p>
                        )}
                      </>
                    ) : (
                      <p className="font-medium">{activePickup.status_label}</p>
                    )}
                  </div>
                  {activePickup.is_recurring && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground flex items-center gap-1">
                        <RepeatIcon className="h-3 w-3" />
                        Recurrencia
                      </p>
                      <p className="font-medium">
                        {activePickup.recurrence_pattern_label}
                      </p>
                    </div>
                  )}
                </div>
                {activePickup.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Notas</p>
                    <p className="text-sm bg-gray-50 rounded-md p-3">{activePickup.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Columna derecha: registro de campo */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-green-600" />
                  Registro de campo
                </CardTitle>
              </CardHeader>
              <CardContent>
                {localDraft ? (
                  <DraftRecordView draft={localDraft} wasteTypeName={wasteTypeName} />
                ) : record ? (
                  <div className="space-y-5">
                    <div className="grid gap-3 text-sm sm:grid-cols-3">
                      <div className="flex items-start gap-2">
                        <User className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-muted-foreground">Operador</p>
                          <p className="font-medium">{record.operator?.name ?? '—'}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Clock className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-muted-foreground">Inicio</p>
                          <p className="font-medium">{formatDateTime(record.started_at)}</p>
                        </div>
                      </div>
                      {record.finished_at && (
                        <div className="flex items-start gap-2">
                          <Clock className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                          <div>
                            <p className="text-muted-foreground">Término</p>
                            <p className="font-medium">{formatDateTime(record.finished_at)}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {record.notes && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Notas del operador</p>
                        <p className="text-sm bg-gray-50 rounded-md p-3">{record.notes}</p>
                      </div>
                    )}

                    {record.items.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Ítems registrados</p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="pb-2 text-left font-medium text-muted-foreground">Residuo</th>
                                <th className="pb-2 text-left font-medium text-muted-foreground">Kilos</th>
                                <th className="pb-2 text-left font-medium text-muted-foreground">Estado</th>
                                <th className="pb-2 text-left font-medium text-muted-foreground">Notas</th>
                                <th className="pb-2 text-left font-medium text-muted-foreground">Fotos</th>
                              </tr>
                            </thead>
                            <tbody>
                              {record.items.map((item, idx) => {
                                const segOption = item.segregation_status
                                  ? getSegregationOption(item.segregation_status)
                                  : null
                                const SegIcon = segOption?.icon
                                return (
                                  <tr key={item.id ?? idx} className="border-b last:border-0">
                                    <td className="py-3 pr-3">
                                      {item.waste_type?.name ?? `Tipo #${item.waste_type_id}`}
                                    </td>
                                    <td className="py-3 pr-3 whitespace-nowrap">
                                      {formatKilos(Number(item.kilos))}
                                    </td>
                                    <td className="py-3 pr-3">
                                      {segOption ? (
                                        <span
                                          className={cn(
                                            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
                                            segOption.selectedClassName,
                                          )}
                                        >
                                          {SegIcon && <SegIcon className="h-3 w-3" />}
                                          {segOption.label}
                                        </span>
                                      ) : (
                                        <span className="text-muted-foreground">—</span>
                                      )}
                                    </td>
                                    <td className="py-3 pr-3 text-muted-foreground">
                                      {item.notes ?? '—'}
                                    </td>
                                    <td className="py-3">
                                      <div className="flex flex-wrap gap-1">
                                        {(item.photos ?? []).map((photo) => (
                                          <button
                                            key={photo.id}
                                            type="button"
                                            onClick={() => setLightboxUrl(photo.photo_url)}
                                            className="h-10 w-10 rounded overflow-hidden border hover:opacity-80 transition-opacity"
                                          >
                                            <img
                                              src={photo.photo_url}
                                              alt=""
                                              className="h-full w-full object-cover"
                                            />
                                          </button>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {record.photos.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Fotos del retiro</p>
                        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                          {record.photos.map((photo) => (
                            <button
                              key={photo.id}
                              type="button"
                              onClick={() => setLightboxUrl(photo.photo_url)}
                              className="aspect-square rounded overflow-hidden border hover:opacity-80 transition-opacity"
                            >
                              <img
                                src={photo.photo_url}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-6 text-center text-muted-foreground">
                    <ClipboardList className="h-8 w-8 mb-2 opacity-30" />
                    <p className="text-sm">Aún no se ha registrado este retiro.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Acciones */}
        {(role === 'admin' || role === 'operator') && (
          <div className="flex flex-wrap gap-3">
            <Button
              className="flex-1"
              onClick={() => navigate(`/pickups/${activePickup.id}/record`)}
              disabled={activePickup.status === 'cancelled'}
            >
              <ClipboardList className="mr-2 h-4 w-4" />
              {activePickup.status === 'completed' ? 'Editar retiro' : 'Registrar retiro'}
            </Button>
            {role === 'admin' && !isShowingCachedPickup && (
              <div className="flex flex-col gap-1">
                <Button
                  variant="destructive"
                  disabled={!activePickup.is_deletable || !isOnline}
                  onClick={() => setConfirmDelete(true)}
                  title={
                    !activePickup.is_deletable
                      ? 'No eliminable: el retiro ya tiene registro, no es futuro o su estado lo impide'
                      : !isOnline
                        ? 'Se requiere conexión para eliminar'
                        : undefined
                  }
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Eliminar retiro
                </Button>
                {!activePickup.is_deletable && (
                  <p className="text-xs text-muted-foreground text-center">
                    Solo retiros futuros sin registro
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={!!lightboxUrl} onOpenChange={(open) => !open && setLightboxUrl(null)}>
        <DialogContent className="max-w-3xl p-2">
          {lightboxUrl && (
            <img src={lightboxUrl} alt="" className="w-full h-auto rounded" />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDelete}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmDelete(false)
            setDeleteError(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar retiro</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar el{' '}
              <strong>Retiro #{activePickup?.id}</strong> programado para el{' '}
              <strong>
                {activePickup
                  ? format(
                      new Date(activePickup.scheduled_date + 'T00:00:00'),
                      "d 'de' MMMM yyyy",
                      { locale: es },
                    )
                  : ''}
              </strong>
              ? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <p className="text-sm text-destructive">{deleteError}</p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDelete(false)
                setDeleteError(null)
              }}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
